import { Component } from '@theme/component';
import { DiscountUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { CURRENCY_DECIMALS, DEFAULT_CURRENCY_DECIMALS, convertMoney, formatMoney } from '@theme/money-formatting';

const STORAGE_KEY = 'shinesty-discount';
const SUPPRESSED_KEY = 'shinesty-discount-suppressed';
const CONFIG_ELEMENT_ID = 'utm-discount-config';
const DEFAULT_DURATION_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * @typedef {object} UtmDiscount
 * @property {boolean} enabled - The merchant's on/off switch. Only enabled entries are rendered.
 * @property {string} discount_code - The code carried by the `utm_discount` URL param.
 * @property {string} discount_value - The discount in the SHOP's currency, major units ("5").
 * @property {string} product_discount_copy - Copy with a `[price]` token, e.g. "Try for [price]".
 * @property {string[]} [selling_plans] - Names of the selling plans the discount may be shown on,
 *   matched against a plan's own name the way `blocks/_purchase-option.liquid` matches its cadence.
 *   Absent or empty means unrestricted, which the announcement bar and the product card teasers
 *   rely on — neither belongs to a selling plan.
 * @property {number} [duration] - Days the code keeps applying after it is seen.
 */

/**
 * @typedef {object} StoredDiscount
 * @property {UtmDiscount} discount
 * @property {number} expiresAt - Epoch milliseconds.
 */

/**
 * The slice of the Cart AJAX API's cart this file reads.
 *
 * @typedef {object} Cart
 * @property {{properties?: Record<string, string> | null, selling_plan_allocation?: {selling_plan?: {name?: string}} | null}[]} [items]
 * @property {{code: string, applicable: boolean}[]} [discount_codes]
 */

/**
 * Carries the shop's enabled UTM discounts and money format, rendered once globally by
 * snippets/scripts.liquid from the `custom.utm_discount_config` metafield.
 *
 * @returns {HTMLElement | null}
 */
function configElement() {
  return document.getElementById(CONFIG_ELEMENT_ID);
}

/**
 * @returns {UtmDiscount[]}
 */
function readConfig() {
  const text = configElement()?.textContent;
  if (!text) return [];

  try {
    const parsed = JSON.parse(text);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Whether a discount may be shown against a given selling plan.
 *
 * A promo is a first-subscription offer, so it belongs to one cadence and not the rest: showing it
 * on a quarterly or annual card advertised a price checkout would not honour (SHINE-832). An element
 * with no plan of its own is never filtered — see `selling_plans` on the typedef.
 *
 * @param {UtmDiscount | null} discount
 * @param {string | null | undefined} sellingPlanName
 * @returns {boolean}
 */
export function discountAllowsSellingPlan(discount, sellingPlanName) {
  const allowed = discount?.selling_plans;
  if (!Array.isArray(allowed) || allowed.length === 0) return true;
  if (!sellingPlanName) return true;

  const wanted = sellingPlanName.trim().toLowerCase();

  return allowed.some((name) => String(name).trim().toLowerCase() === wanted);
}

/**
 * Matches the `utm_discount` URL param (a discount code, e.g. `10dollarsub`)
 * against the shop's discount list, persisting whichever entry matched so it
 * survives navigation to a page without the param, and the browser closing.
 * Returns null wherever the shop renders no config element, which is how the
 * promo is switched off for a non-domestic market.
 *
 * @returns {UtmDiscount | null}
 */
export function resolveDiscount() {
  // snippets/scripts.liquid withholds the config outside the merchant's domestic
  // countries, so a code stored before a market switch must stop applying too.
  if (!configElement()) {
    clearStoredDiscount();
    return null;
  }

  const code = new URLSearchParams(window.location.search).get('utm_discount');

  if (code) {
    const match = readConfig().find((entry) => entry.discount_code?.toLowerCase() === code.toLowerCase());

    if (match) storeDiscount(match);
  }

  if (localStorage.getItem(SUPPRESSED_KEY) === 'true') return null;

  return readStoredDiscount();
}

/**
 * Persisted rather than held in memory so the next page load renders the right price
 * on the server's first paint, instead of flashing the promo until the cart read lands.
 *
 * @param {boolean} suppressed
 */
function setSuppressed(suppressed) {
  if (suppressed) localStorage.setItem(SUPPRESSED_KEY, 'true');
  else localStorage.removeItem(SUPPRESSED_KEY);
}

/**
 * Starts the entry's `duration` window from now — a link visited again restarts it.
 *
 * Suppression is a judgement about one promo against one cart, so arriving on a different
 * promo's link lifts it until the next cart read decides it again.
 *
 * @param {UtmDiscount} discount
 */
function storeDiscount(discount) {
  if (readStoredDiscount()?.discount_code !== discount.discount_code) setSuppressed(false);

  const days = Number(discount.duration);
  /** @type {StoredDiscount} */
  const stored = {
    discount,
    expiresAt: Date.now() + (days > 0 ? days : DEFAULT_DURATION_DAYS) * DAY_MS,
  };

  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
}

/**
 * @returns {UtmDiscount | null}
 */
function readStoredDiscount() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;

  try {
    const { discount, expiresAt } = JSON.parse(stored);

    // Also clears a value written before durations existed, which carries no expiry
    if (!discount || !(expiresAt > Date.now())) {
      clearStoredDiscount();
      return null;
    }

    // A stored discount outlives the merchant disabling its entry by up to its whole
    // duration, so being absent from the rendered config has to end it early.
    if (!isConfigured(discount)) {
      clearStoredDiscount();
      return null;
    }

    return discount;
  } catch {
    clearStoredDiscount();
    return null;
  }
}

/**
 * @param {UtmDiscount} discount
 * @returns {boolean}
 */
function isConfigured(discount) {
  const code = discount.discount_code?.toLowerCase();

  return readConfig().some((entry) => entry.discount_code?.toLowerCase() === code);
}

function clearStoredDiscount() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(SUPPRESSED_KEY);
}

/**
 * @returns {Promise<Cart>}
 */
async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`);
  if (!response.ok) throw new Error(`Cart read failed: ${response.status}`);

  return response.json();
}

/**
 * Whether the cart holds a subscription that spends this discount.
 *
 * A subscription line is the test snippets/cart-products.liquid renders one with — some
 * come from the Shinesty subscription API and carry the property rather than a Shopify
 * selling plan. A line whose plan the discount was never offered on does not spend it,
 * matched the same way the badge is (`discountAllowsSellingPlan`); a line with no plan
 * of its own spends it, which is what a property-only subscription falls back to.
 *
 * The free gift is given rather than bought, so its line never spends the offer even when it
 * is a subscription on the very plan the promo names (SHINE-917).
 *
 * @param {Cart} cart
 * @param {UtmDiscount} discount
 * @returns {boolean}
 */
function hasSubscription(cart, discount) {
  return (cart.items ?? []).some((item) => {
    if (item.properties?.__isFreeItem === 'true') return false;

    const flag = item.properties?.__isSubscription ?? item.properties?._isSubscription;
    if (flag !== 'true' && item.selling_plan_allocation == null) return false;

    return discountAllowsSellingPlan(discount, item.selling_plan_allocation?.selling_plan?.name);
  });
}

/**
 * Reconciles the discount against the cart, on load and on every cart change.
 *
 * The promo is a first-subscription offer, so its copy stops showing while the cart holds a
 * subscription it covers — suppressed rather than discarded, so removing that subscription
 * brings it back for the rest of its duration. The code goes on the cart either way, since it
 * is what makes checkout charge the promo price, and a cart that already held the
 * subscription when the link was followed would otherwise never get it (SHINE-917).
 *
 * @returns {Promise<void>}
 */
async function syncDiscountWithCart() {
  if (window.Shopify?.designMode) return;

  // Called for the URL capture and the market guard, then read past: this is the one
  // caller that has to see a suppressed discount, or nothing would ever lift it.
  resolveDiscount();

  const discount = readStoredDiscount();
  if (!discount) return;

  try {
    const cart = await fetchCart();

    setSuppressed(hasSubscription(cart, discount));
    refreshPromoComponents();

    await applyResolvedDiscount(cart, discount);
  } catch {
    // Leave the cart's codes as they are, so the next cart change or page load retries
  }
}

function refreshPromoComponents() {
  for (const element of document.querySelectorAll('utm-price-component')) {
    if (element instanceof UtmPriceComponent) element.refresh();
  }
}

/**
 * Whether a code already on the cart is one of the shop's OTHER promos.
 *
 * @param {string} entry
 * @param {string} code - The code being applied.
 * @returns {boolean}
 */
function isRivalPromoCode(entry, code) {
  const lower = entry.toLowerCase();
  if (lower === code.toLowerCase()) return false;

  return readConfig().some((configured) => configured.discount_code?.toLowerCase() === lower);
}

/**
 * Puts the stored code on the cart so it actually comes off at checkout, via the Cart AJAX
 * API — the same `cart_update_url` + `discount` list Horizon's own assets/cart-discount.js
 * posts. `discount` REPLACES the cart's codes rather than appending, so the codes already on
 * the given cart are sent back with it.
 *
 * The shop's other promo codes are dropped on the way out. They are competing
 * first-subscription offers, and with two on one cart checkout charges whichever it prefers,
 * so a visitor who follows a second promo link keeps paying the first one's price
 * (SHINE-917).
 *
 * The cart's own codes decide whether there is anything to do, so a code Shopify has not
 * taken yet (an empty cart, conditions not met) is retried on the next cart change or page
 * load, and one the customer removes at checkout goes back on.
 *
 * @param {Cart} cart
 * @param {UtmDiscount} discount
 * @returns {Promise<void>}
 */
async function applyResolvedDiscount(cart, discount) {
  const code = discount.discount_code;
  if (!code) return;

  const existing = (cart.discount_codes ?? []).map((entry) => entry.code);
  const codes = existing.filter((entry) => !isRivalPromoCode(entry, code));
  const onCart = codes.some((entry) => entry.toLowerCase() === code.toLowerCase());

  if (onCart && codes.length === existing.length) return;
  if (!onCart) codes.push(code);

  const response = await fetch(
    Theme.routes.cart_update_url,
    fetchConfig('json', { body: JSON.stringify({ discount: codes.join(',') }) }),
  );
  if (!response.ok) throw new Error(`Discount update failed: ${response.status}`);

  document.dispatchEvent(new DiscountUpdateEvent(await response.json(), CONFIG_ELEMENT_ID));
}

/**
 * Rescales a Liquid money integer for `formatMoney`. Liquid renders every price as hundredths
 * whatever the currency, but `formatMoney` divides by the currency's OWN precision — none for
 * JPY/KRW — so passing one straight through renders it 100x too large.
 *
 * @param {number} liquidPrice
 * @param {number} precision
 * @returns {number}
 */
function toMinorUnits(liquidPrice, precision) {
  return Math.round((liquidPrice / 100) * Math.pow(10, precision));
}

/**
 * @param {string} currency
 * @returns {number}
 */
function currencyPrecision(currency) {
  return CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;
}

/**
 * The discount in the MARKET's currency, in minor units. `discount_value` is quoted in the
 * shop's currency, so it needs `Shopify.currency.rate` applied before it can be combined with
 * a market price — same reason as assets/discount-price.js. Liquid has no exchange rate.
 *
 * @param {UtmDiscount} discount
 * @param {string} currency
 * @returns {number}
 */
function discountMinorUnits(discount, currency) {
  const amount = Number(discount.discount_value);
  if (!Number.isFinite(amount)) return 0;

  return Math.round(convertMoney(amount) * Math.pow(10, currencyPrecision(currency)));
}

/**
 * Whether the promo covers a product, as `util-utm-price-eligible` and `util-product-subscribable`
 * decide it server-side: a covered type, on sale, carrying the merchant's subscribe tag and a
 * selling plan of its own — OrderGroove's being the legacy system.
 *
 * For a card assembled client-side, whose products the server never rendered. The type list rides
 * on the config element so both sides read the one list.
 *
 * @param {any} product - The product, as the Ajax Product API describes it.
 * @returns {boolean}
 */
export function promoCoversProduct(product) {
  const types = configElement()?.dataset.eligibleTypes ?? '';
  if (!types.includes(`|${String(product?.type ?? '').toLowerCase()}|`)) return false;

  const tags = (product?.tags ?? []).map((/** @type {string} */ tag) => String(tag).toLowerCase());
  if (!tags.includes('show subscribe option') || product?.available === false) return false;

  return (product?.selling_plan_groups ?? []).some(
    (/** @type {any} */ group) => !String(group?.app_id ?? '').toLowerCase().includes('ordergroove')
  );
}

/**
 * How many products the discount is charged on.
 *
 * The discount flattens a product to a fixed price, and one payment can buy more than one. A
 * card charging the promo for each says so in `data-utm-per-unit`, which carries the count:
 * two pairs on a $5 code come to $10. Absent, the promo is charged once.
 *
 * @param {HTMLElement} element
 * @returns {number}
 */
function discountedProducts(element) {
  const units = Math.trunc(Number(element.dataset.utmPerUnit));

  return Number.isFinite(units) && units > 0 ? units : 1;
}

/**
 * Swaps in copy from the shop's active UTM discount (see `resolveDiscount`)
 * once a `utm_discount` URL param has been seen. Renders
 * nothing until then, leaving the element's original content (e.g. the
 * regular price) in place.
 *
 * The copy is `data-price-label` if given, else the matched entry's own
 * `product_discount_copy`. Either way its `[price]` token becomes the
 * discount's formatted price — or, with `data-price-difference` (a raw
 * Liquid price integer), what the discount saves against that
 * price, for "Save X" copy.
 *
 * Sets `data-replaced` on itself once it has swapped its contents, so the
 * promo state can be styled — e.g. `utm-price-component[data-replaced]`.
 *
 * Usage:
 *   <utm-price-component data-eligible="true"></utm-price-component>
 *   <utm-price-component data-eligible="true" data-price-label="[price]"></utm-price-component>
 *   <utm-price-component
 *     data-eligible="true"
 *     data-price-label="Save [price]"
 *     data-price-difference="{{ base_price }}"
 *   ></utm-price-component>
 *
 * @extends {Component}
 */
export class UtmPriceComponent extends Component {
  /** @type {string | undefined} */
  #serverContent;

  static get observedAttributes() {
    return ['data-replaced'];
  }

  connectedCallback() {
    super.connectedCallback();

    this.#render();
  }

  /**
   * Re-render after the Section Rendering API / morph replaces our text
   * (e.g. collection filtering, pagination, the search overlay).
   */
  updatedCallback() {
    super.updatedCallback();
    this.#render();
  }

  /**
   * Re-render after a morph, which strips `data-replaced` on its way to restoring the server's
   * price. The only notice we get: morph's post-update hook reports the parsed response node,
   * so `updatedCallback` never reaches the element actually morphed. Queued because attributes
   * are copied before children.
   *
   * @param {string} name
   * @param {string | null} _oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, _oldValue, newValue) {
    // only the removal, so re-setting it below doesn't loop back in here
    if (name === 'data-replaced' && newValue === null) queueMicrotask(() => this.#render());
  }

  #render() {
    if (this.dataset.eligible !== 'true') {
      return;
    }

    const discount = resolveDiscount();
    if (!discount) return;
    if (!discountAllowsSellingPlan(discount, this.dataset.sellingPlan)) return;

    const template = this.dataset.priceLabel || discount.product_discount_copy || '';
    const currency = window.Shopify?.currency?.active;
    const format = configElement()?.dataset.moneyFormat;
    if (!template || !currency || !format) return;

    const money = formatMoney(this.#amountMinorUnits(discount, currency), format, currency);

    const percent = this.#savingsPercent(discount, currency);
    if (template.includes('[percent]') && percent === '') return;

    // Re-read on any render that is not overwriting our own copy, so a variant
    // change or a filtered re-render updates the price `restore` puts back.
    if (this.dataset.replaced !== 'true') this.#serverContent = this.innerHTML;

    this.innerHTML = ` ${template.replace('[price]', money).replace('[percent]', percent)}`;
    // Styling hook for the promo state, since the original content is now gone
    this.dataset.replaced = 'true';
    this.classList.remove('hidden');
  }

  /**
   * What the promo saves against `data-price-difference`, as a whole percentage.
   * @param {UtmDiscount} discount
   * @param {string} currency
   * @returns {string}
   */
  #savingsPercent(discount, currency) {
    const difference = Number(this.dataset.priceDifference);
    if (!Number.isFinite(difference) || difference <= 0) return '';

    const base = toMinorUnits(difference, currencyPrecision(currency));
    const saved = base - discountMinorUnits(discount, currency) * discountedProducts(this);
    if (saved <= 0) return '';

    return String(Math.round((saved / base) * 100));
  }

  /**
   * Say what this element shows when no promo applies, and price it again from there.
   *
   * For a host that knows the price better than the server did. It has to be told rather than
   * written over: while a promo shows, what is on screen is the promo's copy, and writing
   * there would make that copy the fallback.
   *
   * @param {string} content
   */
  showPrice(content) {
    this.#serverContent = content;
    this.innerHTML = content;
    delete this.dataset.replaced;

    this.#render();
  }

  /**
   * Re-evaluates the promo after a cart change decides whether it still applies
   * (see `syncDiscountWithCart`). Falls back to the server-rendered price, then
   * swaps the promo copy back in if one still resolves — so it survives a
   * subscription being added and then removed.
   */
  refresh() {
    if (this.#serverContent !== undefined) {
      this.innerHTML = this.#serverContent;
      delete this.dataset.replaced;
    }

    this.#render();
  }

  /**
   * @param {UtmDiscount} discount
   * @param {string} currency
   * @returns {number}
   */
  #amountMinorUnits(discount, currency) {
    const promo = discountMinorUnits(discount, currency) * discountedProducts(this);
    const difference = Number(this.dataset.priceDifference);
    if (!Number.isFinite(difference)) return promo;

    return Math.max(0, toMinorUnits(difference, currencyPrecision(currency)) - promo);
  }

  /**
   * @returns {boolean} True while a UTM discount is stored and unexpired.
   */
  static isPromoActive() {
    return resolveDiscount() !== null;
  }

  /**
   * @returns {UtmDiscount | null}
   */
  static resolveDiscount() {
    return resolveDiscount();
  }
}

if (!customElements.get('utm-price-component')) {
  customElements.define('utm-price-component', UtmPriceComponent);
}

// For scripts that cannot import — an inline landing-page script, the console.
// Module code imports from '@theme/utm-price'.
if (typeof Theme !== 'undefined') {
  Theme.utmDiscount = {
    resolve: resolveDiscount,
    clear: clearStoredDiscount,
    isActive: UtmPriceComponent.isPromoActive,
  };
}

document.addEventListener(ThemeEvents.cartUpdate, () => syncDiscountWithCart());

syncDiscountWithCart();
